import React, { useRef, useState } from "react";
import GridComponent from "../components/GridComponent/GridComponent";
import { LeftSideComponent } from "../components/Sidebar/Sidebar";
import GridTitle from "../components/GridComponent/components/GridTitle";
import Typography from "../components/Typography/Typography";
import TimerIcon from "../assets/icons/bx-stopwatch-blue.svg";
import Rocketcon from "../assets/icons/bx-rocket-blue.svg";
import GridLGHidden from "../components/GridComponent/components/GridLGHidden";
import Header from "../components/Header/Header";
import BottomNav from "../components/BottomNav/BottomNav";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Divider from "../components/Divider/Divider";
import InfoIcon from "../assets/icons/bx-info-circle-grey.svg";
import Triangle from "../assets/images/triangle-gray.svg";
import useSessionOverview from "../store/Querys/useSessionOverview";
import useOrganisation from "../store/Querys/useOrganisation";
import { SessionOverviewResponse } from "../store/interface";
import ModeUsage from "../components/ModeUsage/ModeUsage";
import Loading from "../components/Loading/Loading";

const SessionOverview = () => {
  const { data: adminData } = useOrganisation();
  const organizationId = adminData?.organizations?.[0]?.id;
  const { id: feedbackId } = useParams<{ id: string }>();
  const { data, isPending } = useSessionOverview(organizationId, feedbackId);

  return (
    <div className="flex flex-col lg:flex-row bg-[#F5F5FC]">
      <GridLGHidden>
        <Header backgroundColor="[#F5F5FC]" />
      </GridLGHidden>
      {isPending ? (
        <Loading />
      ) : (
        <GridComponent
          leftSideComponent={<LeftSideComponent />}
          containerClassNames={"lg:grid-cols-grid-chat-no-right-lg"}
          mainComponent={<MainComponent data={data} />}
        />
      )}

      <GridLGHidden>
        <BottomNav />
      </GridLGHidden>
    </div>
  );
};

export default SessionOverview;

const MainComponent = ({
  data,
}: {
  data: SessionOverviewResponse | undefined;
}) => {
  const navigate = useNavigate();
  const infoRef = useRef<HTMLDivElement>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const location = useLocation();
  const getQueryParams = () => {
    const urlParams = new URLSearchParams(location.search);
    const name = urlParams.get("name");
    const lastName = urlParams.get("lastName");
    const sessionDate = urlParams.get("session") || "";
    return { name, lastName, sessionDate };
  };

  const { name, lastName, sessionDate } = getQueryParams();

  return (
    <div className="flex flex-1 flex-col h-svh max-lg:h-auto bg-[#F5F5FC] lg:bg-transparent gap-24">
      <GridTitle title="Session overview" backBtnClick={() => navigate(-1)} />

      <div className="bg-white rounded-20 border border-secondary p-24 lg:grid lg:grid-cols-3 flex   max-lg:flex-col max-lg:items-start max-lg:justify-start">
        <div className="max-lg:mb-24">
          <Typography type="Header3">{name + " " + lastName}</Typography>
          <Typography type="Body2" className="!text-[#868686] mt-8">
            Daily session - {sessionDate.replace(/-/g, "/")}
          </Typography>
        </div>
        <div className="flex gap-12 items-center relative max-lg:w-full">
          <div className="p-[10.4px] bg-secondary rounded-2xl">
            <img src={Rocketcon} alt="" className="w-[31.2px] h-[31.2px]" />
          </div>
          <div>
            <Typography type="Body">{data?.practiceScore ?? "-"}/100</Typography>
            <Typography type="Body2" className="!text-[#868686]">
              Overall Score
            </Typography>
          </div>
          <div
            className="bg-[#F5F5FC] relative rounded-full p-[4px] cursor-pointer max-lg:absolute max-lg:right-0 hover:bg-[#EAEBF9]"
            ref={infoRef}
            onClick={() => setTooltipVisible(!tooltipVisible)}
          >
            <img src={InfoIcon} alt="" className="w-16 h-16" />
            {tooltipVisible && (
              <div
                className={
                  "absolute z-10 flex transform items-center lg:right-[140%] lg:top-1/2 lg:-translate-y-1/2 max-lg:left-[-52px] max-lg:top-[20px] max-lg:-translate-x-1/2 max-lg:flex-col-reverse "
                }
              >
                <div
                  className={
                    "w-200 rounded-lg border border-[#EAEBF9] bg-light-grey-8 px-16 py-12"
                  }
                >
                  <Typography type={"Body3"} className={"text-center"}>
                    The overall score is available for sessions with data from
                    active practice modes (e.g., Chat, Roleplays, and Call
                    mode).
                  </Typography>
                </div>
                <img
                  src={Triangle}
                  className="relative z-20 max-lg:left-[65px] max-lg:top-[6px] lg:rotate-180 max-lg:rotate-90"
                  alt=""
                />
              </div>
            )}
          </div>
        </div>
        <GridLGHidden className="w-full">
          <Divider className="w-full" />
        </GridLGHidden>
        <div className="flex gap-12 items-center jus">
          <div className="p-[10.4px] bg-secondary rounded-2xl">
            <img src={TimerIcon} alt="" className="w-[31.2px] h-[31.2px]" />
          </div>
          <div>
            <Typography type="Body">{`${
              data?.totalPracticeTime.days && data?.totalPracticeTime.days !== 0
                ? `${data?.totalPracticeTime.days} days, `
                : ""
            }${
              data?.totalPracticeTime.hours &&
              data?.totalPracticeTime.hours !== 0
                ? `${data?.totalPracticeTime.hours} hours, `
                : ""
            }${data?.totalPracticeTime.minutes || "0"} minutes`}</Typography>
            <Typography type="Body2" className="!text-[#868686]">
              Total practice time
            </Typography>
          </div>
        </div>
      </div>

      <div className="flex gap-24 max-lg:flex-col w-full lg:items-start">
        <ModeUsage modesData={data} />

        <div className="bg-white rounded-20 border border-secondary p-24 flex-1 max-lg:mb-[100px]">
          <Typography type="Header3">Problematic topics</Typography>
          <Typography type="Body2" className="!text-[#868686] mt-8">
            The list of problematic topics mentioned in the feedback.
          </Typography>

          <div className="flex flex-wrap gap-16 mt-24">
            {data?.grammarTopics && data.grammarTopics.length > 0 ? (
              data.grammarTopics.map((topic, index) => (
                <div key={index} className="bg-secondary rounded-20 p-8">
                  <Typography type="Body2" className="opacity-60">
                    {topic.charAt(0).toUpperCase() + topic.slice(1)}
                  </Typography>
                </div>
              ))
            ) : (
              <Typography type="Body2" className="!text-[#868686]">
                No problematic topics found.
              </Typography>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
